import {Routes, Route} from 'react-router-dom'
import DashboardComponent from './components/Dashboard/DashboardComponent'
import ErrorComponent from './components/Error/ErrorComponent'
import AdminPanel from './AdminPanel/AdminPanel'
import ProjectListLanding from './AdminPanel/ProjectList/ProjectListLanding'
import ProjectDetailsComponent from './AdminPanel/ProjectDetails/ProjectDetailsComponent'
import PanelHeader from './components/common/PanelHeader/PanelHeader'
import CreateClientComponent from './AdminPanel/CreateClient/CreateClientComponent'
import CustomProjectDetails from './AdminPanel/customSettings/CustomProjectDetails'
import CustomBox from './components/common/CustomBox'
import PropTypes from 'prop-types'
import FaqApp from './components/FAQ/FaqApp'
import TimeSeriesDashboardComponent from './TimeSeriesDashboard/TimeSeriesDashboardComponent'
import {usePermission} from './store/hooks/permission'
import {ThemeProvider} from '@mui/material'
import pTheme from './themes/pTheme/pTheme'
import {StyledSpaceBox} from './components/Dashboard/charts/PopulationDensityChart/Tooltips/Styles'
export function Routing({
  isAdminPanelOpen,
  setIsAdminPanelOpen,
  isTimeSeriesOpen,
  setIsTimeSeriesOpen,
  setIsFAQopen,
  deliveryAdmin,
  roles,
  superAdmin,
  customerSupport,
  viewer,
  showChoropleth,
  setShowChoropleth
}) {
  Routing.propTypes = {
    isAdminPanelOpen: PropTypes.bool,
    setIsAdminPanelOpen: PropTypes.func,
    isTimeSeriesOpen: PropTypes.bool,
    setIsTimeSeriesOpen: PropTypes.func,
    setIsFAQopen: PropTypes.func,
    deliveryAdmin: PropTypes.bool,
    roles: PropTypes.array,
    superAdmin: PropTypes.bool,
    customerSupport: PropTypes.bool,
    viewer: PropTypes.bool,
    showChoropleth: PropTypes.bool,
    setShowChoropleth: PropTypes.func
  }
  const {permission} = usePermission()

  const isTrendAnalysisAllowed = permission?.healthEquityConditionList?.trendAnalysisPermissions?.[0] === 1
  const isUserfeedbackAllowed = permission?.healthEquityConditionList?.userfeedbackFlagPermissions?.[0] === 1
  return (
    <CustomBox>
      <ThemeProvider theme={pTheme}>
        <PanelHeader />
      </ThemeProvider>
      <Routes>
        {(viewer || deliveryAdmin || superAdmin || customerSupport) && (
          <>
            <Route
              path='/'
              element={
                <DashboardComponent
                  isAdminPanelOpen={isAdminPanelOpen}
                  setIsAdminPanelOpen={setIsAdminPanelOpen}
                  admin={deliveryAdmin}
                  showChoropleth={showChoropleth}
                  setShowChoropleth={setShowChoropleth}
                />
              }
            />
            <Route
              path='/faq'
              element={
                <ThemeProvider theme={pTheme}>
                  <FaqApp setIsFAQopen={setIsFAQopen} />
                  {isUserfeedbackAllowed && <StyledSpaceBox size={40} />}
                </ThemeProvider>
              }
            />
            {(isTrendAnalysisAllowed || deliveryAdmin || superAdmin) && (
              <Route
                path='/timeseries'
                element={
                  <TimeSeriesDashboardComponent
                    setIsTimeSeriesOpen={setIsTimeSeriesOpen}
                    isTimeSeriesOpen={isTimeSeriesOpen}
                  />
                }
              />
            )}
          </>
        )}
        {deliveryAdmin || superAdmin || customerSupport ? (
          <>
            <Route path='/adminpanel' element={<AdminPanel setIsAdminPanelOpen={setIsAdminPanelOpen} />} />
            <Route path='/adminpanel/:tab' element={<AdminPanel setIsAdminPanelOpen={setIsAdminPanelOpen} />} />
            {superAdmin || customerSupport ? (
              <>
                <Route
                  path='/adminpanel/createclient'
                  element={<CreateClientComponent setIsAdminPanelOpen={setIsAdminPanelOpen} />}
                />
                <Route
                  path='/adminpanel/client/:clientId/details'
                  element={<CreateClientComponent setIsAdminPanelOpen={setIsAdminPanelOpen} />}
                />
                <Route
                  path='/adminpanel/customsettings'
                  element={<CustomProjectDetails setIsAdminPanelOpen={setIsAdminPanelOpen} />}
                />
              </>
            ) : (
              roles.length > 0 && (
                <Route path='*' element={<ErrorComponent errorCode={401} errorMsg={'Access Denied'} />} />
              )
            )}
            <Route
              path='/adminpanel/client/:clientId'
              element={
                <ProjectListLanding isAdminPanelOpen={isAdminPanelOpen} setIsAdminPanelOpen={setIsAdminPanelOpen} />
              }
            />
            <Route
              path='/adminpanel/client/:clientId/create'
              element={
                <ProjectDetailsComponent
                  isAdminPanelOpen={isAdminPanelOpen}
                  setIsAdminPanelOpen={setIsAdminPanelOpen}
                />
              }
            />
            <Route
              path='/adminpanel/client/:clientId/project/:projectId'
              element={
                <ProjectDetailsComponent
                  isAdminPanelOpen={isAdminPanelOpen}
                  setIsAdminPanelOpen={setIsAdminPanelOpen}
                />
              }
            />
          </>
        ) : (
          roles.length > 0 && <Route path='*' element={<ErrorComponent errorCode={401} errorMsg={'Access Denied'} />} />
        )}

        {roles.length > 0 && (
          <Route
            path='*'
            element={<ErrorComponent errorCode={404} errorMsg={"Sorry, we couldn't find that page."} />}
          />
        )}
      </Routes>
    </CustomBox>
  )
}

/**
 * Routing Component
 *
 * This component defines the routing structure for the application using React Router.
 * It dynamically renders routes based on user roles and permissions, such as `admin`,
 * `superAdmin`, `customerSupport` and `viewer`. The component also integrates Material-UI's `ThemeProvider`
 * for consistent theming across the application.
 *
 * Props:
 * @param {boolean} isAdminPanelOpen - Indicates if the admin panel is open.
 * @param {function} setIsAdminPanelOpen - Function to toggle the admin panel state.
 * @param {boolean} isTimeSeriesOpen - Indicates if the time series dashboard is open.
 * @param {function} setIsTimeSeriesOpen - Function to toggle the time series dashboard state.
 * @param {function} setIsFAQopen - Function to toggle the FAQ section state.
 * @param {boolean} deliveryAdmin - Indicates if the user has deliveryAdmin privileges.
 * @param {Array} roles - List of roles assigned to the user.
 * @param {boolean} superAdmin - Indicates if the user has super admin privileges.
 * @param {boolean} customerSupport - Indicates if the user has customer support privileges.
 * @param {boolean} viewer - Indicates if the user has viewer privileges.
 * @param {boolean} showChoropleth - Indicates if the choropleth map is visible.
 * @param {function} setShowChoropleth - Function to toggle the visibility of the choropleth map.
 *
 * Summary:
 * The `Routing` component manages the application's navigation and access control.
 * It conditionally renders routes based on user roles and permissions, ensuring
 * secure and role-specific access to various sections of the application. It also
 * incorporates theming and layout components for a consistent user experience.
 */
